/**
 * AutomationDialogHeaderByContext — header variante por contexto (Sprint 3 / BUG-V2-058).
 *
 * Usado dentro do dialog shell (AutomationFormDialog) opcionalmente — cada
 * form filho decide quando incluir. Pattern shared pra manter consistência
 * visual entre os 3 forms (Workflow, Questionario, TaskType).
 *
 * Conteúdo:
 *   - Linha 1: título dinâmico ("Nova automação em <contexto>" ou "Editar")
 *   - Linha 2: <AutomationContextoBadge> + breadcrumb opcional
 *
 * Layout segue padrão dos outros DialogHeaders do app (mesma fonte, mesmo
 * spacing — não cria componente customizado).
 */

import { DialogTitle, DialogDescription } from '@/components/ui';

import { AutomationContextoBadge } from './AutomationContextoBadge';
import type { AutomationContexto } from './types';

interface AutomationDialogHeaderByContextProps {
  contexto: AutomationContexto;
  /** true se está editando automação existente; false se criando nova. */
  isEditing?: boolean;
  /** Nome da automação sendo editada (só usado quando isEditing=true). */
  editingNome?: string;
}

function actionLabel(isEditing: boolean): string {
  return isEditing ? 'Editar automação' : 'Nova automação';
}

function contextoActionVerb(kind: AutomationContexto['kind']): string {
  switch (kind) {
    case 'workflow':
      return 'no workflow';
    case 'task_type':
      return 'no tipo de atividade';
    case 'questionario':
      return 'no questionário';
  }
}

export function AutomationDialogHeaderByContext({
  contexto,
  isEditing = false,
  editingNome,
}: AutomationDialogHeaderByContextProps) {
  return (
    <div data-testid={`automation-dialog-header-${contexto.kind}`} className="space-y-1.5">
      <DialogTitle>
        {isEditing && editingNome
          ? `Editar "${editingNome}"`
          : `${actionLabel(isEditing)} ${contextoActionVerb(contexto.kind)}`}
      </DialogTitle>
      <DialogDescription>
        <AutomationContextoBadge contexto={contexto} size="sm" />
      </DialogDescription>
    </div>
  );
}
